import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import fileFolders from "./fileFolders.json";
import "./FilesManagement.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faDownload,
  faTrash,
  faList,
  faBorderAll,
  faRotateRight,
  faChevronLeft,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

/* =========================
   Constants & Helpers
   ========================= */
const API_BASE_URL = "https://lttsbackend.axiomos.in";

const HOSTINGER_ROOT_URL = "https://axiomos.in/LttsApp";

const buildFileUrl = (folder, fileName) => {
  return `${HOSTINGER_ROOT_URL}/${encodeURIComponent(
    folder,
  )}/${encodeURIComponent(fileName)}`;
};

const isImageFile = (name = "") => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
const isPdfFile = (name = "") => /\.pdf$/i.test(name);
const isDocFile = (name = "") => /\.(doc|docx)$/i.test(name);

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

const FilesManagement = () => {
  const [selectedFolder, setSelectedFolder] = useState("uauc");
  const [files, setFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    files: [],
  });

  const folders = fileFolders.folders || [];

  const previewFile = useMemo(
    () =>
      previewIndex !== null && previewIndex >= 0 && previewIndex < files.length
        ? files[previewIndex]
        : null,
    [previewIndex, files],
  );

  const currentFolderLabel = useMemo(() => {
    const found = folders.find((f) => f.key === selectedFolder);
    return found ? found.label : selectedFolder;
  }, [folders, selectedFolder]);

  /* =========================
     API Calls
     ========================= */
  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setPreviewIndex(null);

      const res = await axios.get(`${API_BASE_URL}/ftp/list/${selectedFolder}`);
      const data = res.data || [];

      const normalised = data.map((item) => {
        if (typeof item === "string") {
          const name = item;
          return {
            name,
            size: null,
            url: buildFileUrl(selectedFolder, name),
          };
        }

        const name = item.name || item.fileName;
        const url = item.url || buildFileUrl(selectedFolder, name);

        return { ...item, name, url };
      });

      setFiles(normalised);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load files. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedFolder]);

  const deleteFile = async (fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/ftp/delete/${selectedFolder}/${fileName}`,
      );
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  const downloadFile = (fileName) => {
    // Directly from Hostinger static URL
    const url = buildFileUrl(selectedFolder, fileName);
    window.open(url, "_blank");
  };

  const toggleFileSelection = (fileName) => {
    setSelectedFiles((prev) =>
      prev.includes(fileName)
        ? prev.filter((f) => f !== fileName)
        : [...prev, fileName],
    );
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map((f) => f.name));
    }
  };

  const deleteSelectedFiles = async () => {
    if (selectedFiles.length === 0) return;

    if (!window.confirm(`Delete ${selectedFiles.length} selected file(s)?`))
      return;

    try {
      await Promise.all(
        selectedFiles.map((file) =>
          axios.delete(`${API_BASE_URL}/ftp/delete/${selectedFolder}/${file}`),
        ),
      );

      setSelectedFiles([]);
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert("Bulk delete failed.");
    }
  };

  const executeDelete = async () => {
    if (confirmDelete.files.length === 0) return;

    try {
      await Promise.all(
        confirmDelete.files.map((file) =>
          axios.delete(`${API_BASE_URL}/ftp/delete/${selectedFolder}/${file}`),
        ),
      );

      setSelectedFiles([]);
      setConfirmDelete({ open: false, files: [] });
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  /* =========================
     Preview Handlers
     ========================= */
  const openPreviewAt = (index) => setPreviewIndex(index);
  const closePreview = () => setPreviewIndex(null);
  const goPrev = () => setPreviewIndex((i) => (i > 0 ? i - 1 : i));
  const goNext = () =>
    setPreviewIndex((i) => (i < files.length - 1 ? i + 1 : i));

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  /* =========================
     Preview Renderer
     ========================= */
  const renderPreviewContent = () => {
    if (!previewFile) return null;

    const { name, url } = previewFile;

    if (isImageFile(name)) {
      return <img src={url} alt={name} className="axi-fm-preview-img" />;
    }

    if (isPdfFile(name)) {
      return (
        <iframe src={url} title="PDF Preview" className="axi-fm-preview-pdf" />
      );
    }

    if (isDocFile(name)) {
      return (
        <iframe
          className="axi-fm-preview-pdf"
          title="DOC Preview"
          src={`https://docs.google.com/gview?url=${encodeURIComponent(
            url,
          )}&embedded=true`}
        />
      );
    }

    return <p>No preview available for this file type.</p>;
  };

  /* =========================
     Render
     ========================= */
  return (
    <div className="axi-fm-root">
      {/* Header */}
      <div className="axi-fm-header">
        <h2 className="axi-fm-title">File Management</h2>
        <p className="axi-fm-subtitle">
          Manage files stored on Hostinger FTP by module.
        </p>
      </div>

      {/* Folder Tabs */}
      <div className="axi-fm-folder-tabs">
        {folders.map((folder) => (
          <button
            key={folder.key}
            className={`axi-fm-tab ${
              folder.key === selectedFolder ? "axi-fm-tab--active" : ""
            }`}
            onClick={() => setSelectedFolder(folder.key)}
          >
            {folder.label}
          </button>
        ))}
      </div>

      {/* Card */}
      <div className="axi-fm-card">
        <div className="axi-fm-card-header">
          <div>
            <h3 className="axi-fm-card-title">{currentFolderLabel}</h3>
            <span className="axi-fm-card-meta">
              {loading
                ? "Loading..."
                : `${files.length} file${files.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div className="axi-fm-actions">
            <button
              className={`axi-fm-view-btn ${
                viewMode === "list" ? "axi-fm-view-btn--active" : ""
              }`}
              onClick={() => setViewMode("list")}
            >
              <FontAwesomeIcon icon={faList} />
            </button>

            <button
              className={`axi-fm-view-btn ${
                viewMode === "grid" ? "axi-fm-view-btn--active" : ""
              }`}
              onClick={() => setViewMode("grid")}
            >
              <FontAwesomeIcon icon={faBorderAll} />
            </button>

            <button
              className="axi-fm-refresh"
              onClick={fetchFiles}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </button>

            <button
              className="axi-fm-icon-btn axi-fm-danger axi-fm-bulk-delete-btn"
              onClick={() =>
                selectedFiles.length > 0 &&
                setConfirmDelete({ open: true, files: [...selectedFiles] })
              }
              disabled={selectedFiles.length === 0}
              title={`Delete Selected (${selectedFiles.length})`}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div className="axi-fm-error">{error}</div>}

        {/* LIST VIEW */}
        {viewMode === "list" && (
          <div className="axi-fm-table-wrapper">
            <table className="axi-fm-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedFiles.length === files.length &&
                        files.length > 0
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>File</th>
                  <th>Size</th>
                  <th style={{ width: "180px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="4">
                      <div className="axi-fm-loader" />
                    </td>
                  </tr>
                )}

                {!loading && files.length === 0 && (
                  <tr>
                    <td colSpan="4" className="axi-fm-empty">
                      No files found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  files.map((file, index) => {
                    const isImg = isImageFile(file.name);
                    const isPdf = isPdfFile(file.name);
                    const isDoc = isDocFile(file.name);

                    return (
                      <tr key={file.name}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedFiles.includes(file.name)}
                            onChange={() => toggleFileSelection(file.name)}
                          />
                        </td>
                        <td className="axi-fm-filecell">
                          {isImg ? (
                            <img
                              src={file.url}
                              className="axi-fm-thumb"
                              alt={file.name}
                            />
                          ) : (
                            <div className="axi-fm-thumb-placeholder">
                              {isPdf ? "PDF" : isDoc ? "DOC" : "FILE"}
                            </div>
                          )}

                          <span className="axi-fm-name">{file.name}</span>
                        </td>

                        <td>{formatSize(file.size)}</td>

                        <td className="axi-fm-actions-col">
                          <button
                            className="axi-fm-icon-btn"
                            onClick={() => openPreviewAt(index)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>

                          <button
                            className="axi-fm-icon-btn"
                            onClick={() => downloadFile(file.name)}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>

                          <button
                            className="axi-fm-icon-btn axi-fm-danger"
                            onClick={() =>
                              setConfirmDelete({
                                open: true,
                                files: [file.name],
                              })
                            }
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* GRID VIEW */}
        {viewMode === "grid" && (
          <div className="axi-fm-grid">
            {loading && <div className="axi-fm-loader" />}

            {!loading && files.length === 0 && (
              <div className="axi-fm-empty">No files found.</div>
            )}

            {!loading &&
              files.map((file, index) => {
                const isImg = isImageFile(file.name);
                const isPdf = isPdfFile(file.name);
                const isDoc = isDocFile(file.name);

                return (
                  <div className="axi-fm-grid-item" key={file.name}>
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      className="axi-fm-grid-checkbox"
                      checked={selectedFiles.includes(file.name)}
                      onClick={(e) => e.stopPropagation()} // stop preview click
                      onChange={() => toggleFileSelection(file.name)}
                    />

                    <div
                      className="axi-fm-grid-thumb"
                      onClick={() => openPreviewAt(index)}
                    >
                      {isImg ? (
                        <img
                          src={file.url}
                          className="axi-fm-grid-img"
                          alt={file.name}
                        />
                      ) : (
                        <div className="axi-fm-grid-placeholder">
                          {isPdf ? "PDF" : isDoc ? "DOC" : "FILE"}
                        </div>
                      )}
                    </div>

                    <div className="axi-fm-grid-name">{file.name}</div>

                    <div className="axi-fm-grid-actions">
                      <button
                        className="axi-fm-icon-btn"
                        onClick={() => openPreviewAt(index)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>

                      <button
                        className="axi-fm-icon-btn"
                        onClick={() => downloadFile(file.name)}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>

                      <button
                        className="axi-fm-icon-btn axi-fm-danger"
                        onClick={() => deleteFile(file.name)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* PREVIEW MODAL */}
      {previewFile && (
        <div className="axi-fm-preview-backdrop" onClick={closePreview}>
          <div
            className="axi-fm-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="axi-fm-preview-header">
              <h3>{previewFile.name}</h3>
              <button
                className="axi-fm-close axi-fm-icon-btn"
                onClick={closePreview}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="axi-fm-preview-body">{renderPreviewContent()}</div>

            <div className="axi-fm-preview-footer">
              <button
                className="axi-fm-icon-btn"
                onClick={goPrev}
                disabled={previewIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <span className="axi-fm-counter">
                {previewIndex + 1} / {files.length}
              </span>

              <button
                className="axi-fm-icon-btn"
                onClick={goNext}
                disabled={previewIndex === files.length - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE */}
      {confirmDelete.open && (
        <div
          className="axi-fm-confirm-backdrop"
          onClick={() => setConfirmDelete({ open: false, files: [] })}
        >
          <div
            className="axi-fm-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="axi-fm-confirm-title">Confirm Delete</h3>

            <p className="axi-fm-confirm-text">
              Are you sure you want to delete{" "}
              <strong>{confirmDelete.files.length}</strong> file
              {confirmDelete.files.length === 1 ? "" : "s"}?
            </p>

            <div className="axi-fm-confirm-actions">
              <button
                className="axi-fm-btn"
                onClick={() => setConfirmDelete({ open: false, files: [] })}
              >
                Cancel
              </button>

              <button className="axi-fm-btn-danger" onClick={executeDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesManagement;
